import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Est8 Media';
const DEFAULT_TITLE = `${SITE_NAME} — Real Estate Photography Booking & Workflow Software`;

// Static route → page-name map. Dynamic segments are handled below.
const STATIC_ROUTE_TITLES = {
  '/': null,                       // landing page: use DEFAULT_TITLE
  '/app': 'Dashboard',
  '/app/dashboard': 'Dashboard',
  '/app/listings': 'Listings',
  '/app/clients': 'Clients',
  '/app/appointments': 'Appointments',
  '/app/calendar': 'Calendar',
  '/app/tasks': 'Tasks',
  '/app/team': 'Team',
  '/app/products': 'Products & Pricing',
  '/app/settings': 'Settings',
  '/app/order-form': 'Order Form',
  '/app/notifications': 'Notifications',
  '/app/payroll': 'Payroll',
  '/app/admin': 'Admin',
  '/login': 'Sign in',
  '/signup': 'Get started',
  '/forgot-password': 'Reset your password',
  '/client-portal': 'Client portal',
};

// Patterns for dynamic public/parameterized routes.
const DYNAMIC_PATTERNS = [
  { test: /^\/book\/[^/]+\/?$/, label: 'Book a photoshoot' },
  { test: /^\/order\/[^/]+\/?$/, label: 'Place your order' },
  { test: /^\/property\/[^/]+\/?$/, label: 'Property website' },
  { test: /^\/share\/[^/]+\/?$/, label: 'Shared listing' },
  { test: /^\/app\/listings\/edit\/[^/]+\/?$/, label: 'Edit listing' },
  { test: /^\/app\/clients\/[^/]+\/?$/, label: 'Client' },
  { test: /^\/app\/team\/[^/]+\/?$/, label: 'Team member' },
];

const computeTitle = (pathname, overrideLabel) => {
  if (overrideLabel) return `${overrideLabel} | ${SITE_NAME}`;

  if (pathname in STATIC_ROUTE_TITLES) {
    const label = STATIC_ROUTE_TITLES[pathname];
    return label ? `${label} | ${SITE_NAME}` : DEFAULT_TITLE;
  }

  const dyn = DYNAMIC_PATTERNS.find((p) => p.test.test(pathname));
  if (dyn) return `${dyn.label} | ${SITE_NAME}`;

  return DEFAULT_TITLE;
};

/**
 * Keeps document.title in sync with the current route. Mount once near the
 * router root. Components can override the title for their specific page by
 * passing an explicit label.
 */
export const usePageTitle = (overrideLabel) => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = computeTitle(pathname, overrideLabel);
  }, [pathname, overrideLabel]);
};

// Helper for one-off pages that want full control (e.g. EditListingPage).
export const setPageTitle = (label) => {
  document.title = label ? `${label} | ${SITE_NAME}` : DEFAULT_TITLE;
};

// Re-exported so consumers can branch on whether a particular path is known.
export { SITE_NAME, DEFAULT_TITLE };
